import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"

export type ThemePreference = "light" | "dark" | "system"
export type ResolvedTheme = Exclude<ThemePreference, "system">

export type ThemePreferenceAdapter = {
    read(): Promise<ThemePreference | null>
    write(preference: ThemePreference): Promise<ThemePreference>
}

type ThemePreferenceContextValue = {
    preference: ThemePreference
    resolvedTheme: ResolvedTheme
    isSaving: boolean
    setPreference(preference: ThemePreference): Promise<void>
}

const storageKey = "takt-theme"
const systemDarkQuery = "(prefers-color-scheme: dark)"
const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null)

function isThemePreference(value: unknown): value is ThemePreference {
    return value === "light" || value === "dark" || value === "system"
}

function systemPrefersDark() {
    return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(systemDarkQuery).matches
    )
}

export function getCachedThemePreference(): ThemePreference {
    if (typeof window === "undefined") return "system"

    try {
        const stored = window.localStorage.getItem(storageKey)
        return isThemePreference(stored) ? stored : "system"
    } catch {
        return "system"
    }
}

export function applyThemePreference(preference: ThemePreference) {
    if (typeof document === "undefined") return

    const dark = preference === "dark" || (preference === "system" && systemPrefersDark())
    document.documentElement.classList.toggle("dark", dark)
    document.documentElement.style.colorScheme = dark ? "dark" : "light"

    try {
        window.localStorage.setItem(storageKey, preference)
    } catch {
        // The theme still applies when browser storage is unavailable.
    }
}

export function initializeThemePreference() {
    applyThemePreference(getCachedThemePreference())
}

export function ThemePreferenceProvider({
    adapter,
    children,
}: {
    adapter: ThemePreferenceAdapter
    children: ReactNode
}) {
    const initialPreference = getCachedThemePreference()
    const [preference, setPreferenceState] = useState<ThemePreference>(initialPreference)
    const [systemDark, setSystemDark] = useState(systemPrefersDark)
    const [isSaving, setIsSaving] = useState(false)
    const preferenceRef = useRef(initialPreference)
    const confirmedPreferenceRef = useRef(initialPreference)
    const revisionRef = useRef(0)
    const synchronizationRef = useRef(0)
    const pendingWritesRef = useRef(0)
    const writeQueueRef = useRef<Promise<void>>(Promise.resolve())

    const commitPreference = useCallback((nextPreference: ThemePreference) => {
        preferenceRef.current = nextPreference
        setPreferenceState(nextPreference)
        applyThemePreference(nextPreference)
    }, [])

    const synchronize = useCallback(async () => {
        if (pendingWritesRef.current > 0) return
        const revision = revisionRef.current
        const synchronization = ++synchronizationRef.current

        try {
            const remotePreference = await adapter.read()
            if (
                remotePreference === null ||
                revision !== revisionRef.current ||
                synchronization !== synchronizationRef.current
            ) {
                return
            }
            confirmedPreferenceRef.current = remotePreference
            commitPreference(remotePreference)
        } catch {
            // Keep the cached preference when the account setting cannot be reached.
        }
    }, [adapter, commitPreference])

    const setPreference = useCallback(
        async (nextPreference: ThemePreference) => {
            if (nextPreference === preferenceRef.current && pendingWritesRef.current === 0) return

            const revision = ++revisionRef.current
            pendingWritesRef.current += 1
            setIsSaving(true)
            commitPreference(nextPreference)

            const write = writeQueueRef.current.then(() => adapter.write(nextPreference))
            writeQueueRef.current = write.then(
                () => undefined,
                () => undefined,
            )

            try {
                const persistedPreference = await write
                confirmedPreferenceRef.current = persistedPreference
                if (revision === revisionRef.current) commitPreference(persistedPreference)
            } catch (error) {
                if (revision === revisionRef.current) commitPreference(confirmedPreferenceRef.current)
                throw error
            } finally {
                pendingWritesRef.current -= 1
                if (pendingWritesRef.current === 0) setIsSaving(false)
            }
        },
        [adapter, commitPreference],
    )

    useEffect(() => {
        applyThemePreference(preferenceRef.current)
        void synchronize()

        const handleFocus = () => void synchronize()
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") void synchronize()
        }
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== storageKey || !isThemePreference(event.newValue) || pendingWritesRef.current > 0) return
            revisionRef.current += 1
            confirmedPreferenceRef.current = event.newValue
            commitPreference(event.newValue)
        }

        window.addEventListener("focus", handleFocus)
        window.addEventListener("storage", handleStorage)
        document.addEventListener("visibilitychange", handleVisibilityChange)
        return () => {
            window.removeEventListener("focus", handleFocus)
            window.removeEventListener("storage", handleStorage)
            document.removeEventListener("visibilitychange", handleVisibilityChange)
        }
    }, [commitPreference, synchronize])

    useEffect(() => {
        if (typeof window.matchMedia !== "function") return

        const media = window.matchMedia(systemDarkQuery)
        const handleChange = () => {
            setSystemDark(media.matches)
            if (preferenceRef.current === "system") applyThemePreference("system")
        }
        media.addEventListener("change", handleChange)
        return () => media.removeEventListener("change", handleChange)
    }, [])

    const resolvedTheme = preference === "system" ? (systemDark ? "dark" : "light") : preference

    return (
        <ThemePreferenceContext value={{ preference, resolvedTheme, isSaving, setPreference }}>
            {children}
        </ThemePreferenceContext>
    )
}

export function useThemePreference() {
    const value = useContext(ThemePreferenceContext)
    if (!value) throw new Error("useThemePreference must be used within ThemePreferenceProvider")
    return value
}
